"use client";

import React, { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { Box, Button, Card, Stack, SxProps, Theme, Typography, useScrollTrigger } from "@mui/material";
import { ChevronLeft, Inventory2Outlined, LocalShippingOutlined, StorefrontOutlined } from "@mui/icons-material";

import OfferBadge from "@/app/(main)/products/[...slugs]/components/OfferBadge";

import { useProductPageContext } from "../Providers/useProductPageContext";
import { useCartVariantActions } from "../Providers/useCartVariantActions";

import { useAppContext } from "@/app/(main)/_components/AppProvider";
import ErrorMessage from "@/app/(main)/_components/ErrorMessage";
import { useBottomBar } from "@/app/(main)/_components/BottomBar/BottomBarProvider";
import { useBreakPoints } from "@/lib/useBreakPoints";
import ShieldTickBoldIcon from "@/app/_components/Icons/ShieldTickBoldIcon";
import { CartPricingMode, SVariantDto } from "@/__generated__/sdk";
import { useCartPricingMode, useCartProductItems } from "@/redux/features/cart/hooks";

import NoVariantsSection from "./NoVariantsSection";
import ProductMobileBottomBar from "./ProductMobileBottomBar";
import ProductSellersSheet from "./ProductSellersSheet";
import ProductVariantAddToCartSection from "./ProductVariantAddToCartSection";
import SafeBuy from "./SafeBuy";
import VariantPrice from "./VariantPrice";
import {
  SellerOffer,
  SellerOfferFilter,
  getSellerOffersForVariants,
  getVariantSellerOffers,
  sellerDiscountAmount,
} from "../sellerOffers";

type Props = {
  sx?: SxProps<Theme>;
};

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SummaryRow({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        px: 1.5,
        py: 1,
        borderTop: "1px solid",
        borderColor: "rgba(33, 33, 33, 0.06)",
        color: "grey.800",
        minWidth: 0,
      }}
    >
      {icon ? <Box sx={{ display: "inline-flex", color: "grey.600", fontSize: 18, flexShrink: 0 }}>{icon}</Box> : null}
      <Typography variant="body2" sx={{ fontSize: "0.8125rem", lineHeight: 1.6, minWidth: 0 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default function ProductVariantsSection({ sx }: Props) {
  const pathname = usePathname();
  const trigger = useScrollTrigger();

  const { showContent, resetBottomBar } = useBottomBar();
  const { hasBanner, bannerOptions } = useAppContext();
  const { mdAndUp } = useBreakPoints();
  const context = useProductPageContext();

  const [sellersSheet, setSellersSheet] = useState<SellerOfferFilter | null>(null);

  const hasVariants = Boolean(context?.variants?.length);
  const anyVariantAvailableToSell = Boolean(context?.anyVariantAvailableToSell);

  const productId = context?.productId;
  const hasAnyOffer = Boolean(context?.hasAnyOffer);

  const attributeGroups = context?.attributeGroups ?? [];
  const selectedAttrs = context?.selectedAttrs ?? {};
  const variantToShow = context?.variantToShow;
  const selectedVariant = context?.selectedVariant;
  const matchingVariants = context?.matchingVariants ?? [];
  const tempError = context?.tempError;
  const hasSelectedAttrs = Object.entries(selectedAttrs).length > 0;

  const missingNames = attributeGroups.filter((group) => !selectedAttrs[group.name]).map((group) => group.name);
  const attributesComplete = missingNames.length === 0;

  const offers = useMemo(() => getSellerOffersForVariants(matchingVariants), [matchingVariants]);
  const cartItems = useCartProductItems(productId ?? "");
  const cartPricingMode = useCartPricingMode();

  const featured = useMemo(() => {
    const cartItem = cartItems.find((item) => item.quantity > 0);
    if (cartItem) {
      // Only pin cart when it matches the current attribute combo
      const cartVariant = matchingVariants.find((variant) => variant.id === Number(cartItem.variantId));
      if (cartVariant) {
        const cartOffers = getVariantSellerOffers(cartVariant);
        const inCart =
          cartOffers.find((offer) => !cartPricingMode || cartPricingMode === offer.mode) ?? cartOffers[0];
        if (inCart) return inCart;
      }
    }
    return offers[0] ?? null;
  }, [offers, cartItems, cartPricingMode, matchingVariants]);

  const targetVariant = (featured?.variant as SVariantDto | undefined) ?? (hasSelectedAttrs ? (selectedVariant ?? null) : variantToShow);
  const maxBasketSize = targetVariant?.offer?.maxBasketCount ?? targetVariant?.maxBasketCount ?? 0;
  const { handleQuantityChange, isPending } = useCartVariantActions(productId ?? "", targetVariant?.id, maxBasketSize);

  const cashCount = offers.filter((offer) => offer.mode === CartPricingMode.Cash).length;
  const creditCount = offers.filter((offer) => offer.mode === CartPricingMode.Credit).length;
  const discountAmount = featured ? sellerDiscountAmount(featured) : null;
  const showCreditLink = creditCount > 0 && (cashCount > 0 || creditCount > 1);

  const openSellers = (filter: SellerOfferFilter = "all") => {
    if (mdAndUp) {
      scrollToId(filter === "credit" && cashCount > 0 ? "seller-offers-credit" : "seller-offers");
      return;
    }
    setSellersSheet(filter);
  };

  /**
   * Clear the bottom bar when the URL changes.
   *
   * This effect should not depend on unstable provider functions.
   */
  useEffect(() => {
    resetBottomBar();
  }, [pathname]);

  /**
   * Clear the bottom bar when this component is unmounted.
   *
   * The empty dependency array is intentional.
   */
  useEffect(() => {
    return () => {
      resetBottomBar();
    };
  }, []);

  /** Keep add-to-cart pinned in the mobile bottom bar — no scroll open/close. */
  useEffect(() => {
    if (mdAndUp || !anyVariantAvailableToSell || !productId) {
      resetBottomBar();
      return;
    }

    showContent(
      <ProductMobileBottomBar
        selectedAttrs={selectedAttrs}
        attributesComplete={attributesComplete}
        onGuide={() => context?.promptForAttributes()}
        productId={productId}
        variantToShow={variantToShow}
        selectedVariant={selectedVariant}
        handleQuantityChange={handleQuantityChange}
        isPending={isPending}
        tempError={tempError}
        featuredOffer={featured}
        scrollTargetElemetId={attributesComplete ? "variant-selection" : "variant-pickers"}
      />,
      {
        attach: true,
      },
    );
  }, [
    mdAndUp,
    anyVariantAvailableToSell,
    productId,
    variantToShow,
    selectedVariant,
    matchingVariants,
    isPending,
    tempError,
    featured?.key,
    featured?.stockLeft,
    attributesComplete,
  ]);

  if (!context || !hasVariants || !anyVariantAvailableToSell) {
    return <NoVariantsSection />;
  }

  return (
    <Card
      id="variant-selection"
      elevation={0}
      sx={{
        boxShadow: "0 8px 28px rgba(33, 33, 33, 0.06)",
        backgroundColor: "#fff",
        borderRadius: 3,
        position: { xs: "relative", lg: "sticky" },
        top: {
          lg: trigger
            ? `${88 + (hasBanner ? bannerOptions.sx.minHeight.sm : 0)}px`
            : `${158 + (hasBanner ? bannerOptions.sx.minHeight.sm : 0)}px`,
          xs: 0,
        },
        width: "100%",
        p: { xs: 1.5, sm: 2 },
        pt: { xs: hasAnyOffer ? 6 : 1.5, sm: hasAnyOffer ? 6 : 2 },
        height: "fit-content",
        border: "1px solid",
        borderColor: "grey.100",
        ...sx,
      }}
    >
      <OfferBadge active={Boolean(featured?.offerPrice)} variant={targetVariant ?? null} />

      <Stack spacing={1.5}>
        {featured ? (
          <>
            <SellerSummary
              offer={featured}
              sellerCount={offers.length}
              onShowAll={() => openSellers("all")}
            />

            {showCreditLink ? (
              <Button
                fullWidth
                onClick={() => openSellers("credit")}
                endIcon={<ChevronLeft />}
                sx={{
                  justifyContent: "space-between",
                  border: "1px dashed",
                  borderColor: "info.main",
                  borderRadius: 2,
                  color: "info.main",
                  fontWeight: 700,
                  py: 1,
                  px: 1.5,
                }}
              >
                فروشندگان طرف‌قرارداد اعتباری
              </Button>
            ) : null}

            {discountAmount ? (
              <Box
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "error.main",
                  color: "#fff",
                  borderRadius: 50,
                  px: 1.25,
                  py: 0.45,
                  fontWeight: 800,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  display: { xs: "none", md: "block" },
                }}
              >
                {discountAmount.toLocaleString()} تومان تخفیف
              </Box>
            ) : null}

            {/* Mobile: price + ATC live in the sticky bottom bar only. */}
            <Stack
              sx={{ pb: 1, display: { xs: "none", md: "flex" } }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <VariantPrice variant={featured.variant as SVariantDto} sellerOffer={featured} layout="buybox" />
              <SafeBuy />
            </Stack>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <ProductVariantAddToCartSection
                variant={featured.variant as SVariantDto}
                productId={productId ?? ""}
                layout="buybox"
                pricingMode={featured.mode}
                handleQuantityChange={handleQuantityChange}
                isPending={isPending}
              />
            </Box>
          </>
        ) : targetVariant ? (
          <>
            <Stack
              sx={{ pb: 1, display: { xs: "none", md: "flex" } }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <VariantPrice variant={targetVariant} layout="buybox" />
              <SafeBuy />
            </Stack>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <ProductVariantAddToCartSection
                variant={targetVariant}
                productId={productId ?? ""}
                layout="buybox"
                handleQuantityChange={handleQuantityChange}
                isPending={isPending}
              />
            </Box>
          </>
        ) : null}
      </Stack>

      <ErrorMessage error={tempError} />

      <ProductSellersSheet
        open={sellersSheet != null}
        onClose={() => setSellersSheet(null)}
        variants={matchingVariants as SVariantDto[]}
        productId={productId ?? ""}
        initialFilter={sellersSheet ?? "all"}
      />
    </Card>
  );
}

function SellerSummary({
  offer,
  sellerCount,
  onShowAll,
}: {
  offer: SellerOffer;
  sellerCount: number;
  onShowAll: () => void;
}) {
  return (
    <Box sx={{ bgcolor: "#F4F7FB", borderRadius: 2, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ px: 1.5, py: 1.15 }}>
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
          <StorefrontOutlined sx={{ fontSize: 18, color: "grey.600", flexShrink: 0 }} />
          <Typography fontWeight={800} noWrap sx={{ fontSize: "0.9rem" }}>
            {offer.label}
          </Typography>
        </Stack>
        {sellerCount > 1 ? (
          <Button
            onClick={onShowAll}
            size="small"
            endIcon={<ChevronLeft sx={{ fontSize: 16 }} />}
            sx={{ flexShrink: 0, fontWeight: 800, fontSize: "0.75rem", color: "info.main", px: 0.5, minWidth: 0 }}
          >
            همه فروشندگان ({sellerCount.toLocaleString("fa-IR")})
          </Button>
        ) : null}
      </Stack>

      {offer.shippingOriginLabel ? (
        <SummaryRow icon={<LocalShippingOutlined sx={{ fontSize: 18 }} />}>{offer.shippingOriginLabel}</SummaryRow>
      ) : null}
      <SummaryRow icon={<Inventory2Outlined sx={{ fontSize: 18 }} />}>موجود در انبار فروشنده</SummaryRow>
      {offer.guaranteeTitle ? <SummaryRow icon={<ShieldTickBoldIcon />}>{offer.guaranteeTitle}</SummaryRow> : null}
      {offer.stockLeft != null ? (
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <SummaryRow>
            <Typography component="span" variant="body2" color="error.main" fontWeight={700} sx={{ fontSize: "0.8125rem" }}>
              تنها {offer.stockLeft.toLocaleString("fa-IR")} عدد باقی مانده
            </Typography>
          </SummaryRow>
        </Box>
      ) : null}
    </Box>
  );
}
